/**
 * Which map modes are on offer, and what to do when one goes away.
 *
 * The toolbar filtered its buttons and the panel kept its own `mode`
 * string, and nothing tied the two together. So a GM demoted mid-Build,
 * or a `deployment` lock flipped while somebody was deploying, left the
 * panel in a mode with no button - still building, still deploying, and
 * unable to leave. Leaving a mode is also what tells the shell to give
 * the combat log its column back, so the log stayed shut with nothing
 * on screen that explained why (v8.36.0).
 *
 * Pure, so the rule can be stated once and tested without a toolbar.
 */
export interface ModeDef {
  key: string;
  /** Only a GM may use it - unless `lock` is off (see below). */
  gmOnly?: boolean;
  /**
   * The house lock that, when OPEN, lets a non-GM in anyway. Deploy is
   * the case: a table may hand deployment to its players.
   */
  lock?: string;
}

export interface ModeGrants {
  /** An explicit whitelist - the map editor offers Build alone. */
  allowed?: readonly string[];
  isGm: boolean;
  isLocked: (lock: string) => boolean;
}

/** The keys whose buttons should be on the toolbar right now. */
export function availableModeKeys(modes: readonly ModeDef[], grants: ModeGrants): string[] {
  const { allowed, isGm, isLocked } = grants;
  return modes
    .filter((m) => !allowed || allowed.includes(m.key))
    .filter((m) => !m.gmOnly || isGm || (m.lock != null && !isLocked(m.lock)))
    .map((m) => m.key);
}

/**
 * The mode to actually be in: the current one if it is still offered,
 * otherwise the first that is. `play` is the floor - a map with no
 * modes at all is still a map you can look at.
 */
export function settleMode(current: string, available: readonly string[]): string {
  if (available.includes(current)) return current;
  return available[0] ?? 'play';
}
